import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDarkMode } from '../../contexts/DarkModeContext'
import DeleteAccountModal from './DeleteAccountModal'
import './Settings.scss'

type SettingsTab = 'appearance' | 'account'

function Settings() {
  const navigate = useNavigate()
  const { themeMode, setTheme } = useDarkMode()
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false)
  const [activeTab, setActiveTab] = useState<SettingsTab>('appearance')

  const handleThemeChange = (mode: 'system' | 'light' | 'dark') => {
    setTheme(mode)
  }

  const handleDone = () => {
    navigate(-1)
  }

  const tabs = [
    {
      id: 'appearance' as SettingsTab,
      label: 'Appearance',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/>
          <line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      ),
    },
    {
      id: 'account' as SettingsTab,
      label: 'Account',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
    },
  ]

  return (
    <div className="settings-page">
      <div className="settings-wrapper">
        <div className="settings-header">
          <h1 className="settings-title">Settings</h1>
          <p className="settings-subtitle">Manage your application preferences</p>
        </div>

        <div className="settings-layout">
          {/* Sidebar Navigation */}
          <div className="settings-sidebar">
            <nav className="settings-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="settings-nav-icon">{tab.icon}</span>
                  <span className="settings-nav-label">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="settings-content">
            <div className="settings-card">
              {/* Appearance Tab */}
              {activeTab === 'appearance' && (
                <div className="settings-section">
                  <div className="section-header">
                    <h3 className="section-title">Appearance</h3>
                    <p className="section-description">Choose how JobsReadyAI looks to you</p>
                  </div>
                  
                  <div className="theme-options">
                    <label className={`theme-option ${themeMode === 'system' ? 'checked' : ''}`}>
                      <input
                        type="radio"
                        name="theme"
                        value="system"
                        checked={themeMode === 'system'}
                        onChange={() => handleThemeChange('system')}
                      />
                      <div className="theme-option-content">
                        <div className="theme-option-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                            <line x1="8" y1="21" x2="16" y2="21"/>
                            <line x1="12" y1="17" x2="12" y2="21"/>
                          </svg>
                        </div>
                        <div className="theme-option-text">
                          <div className="theme-option-title">Use system default</div>
                          <div className="theme-option-description">Match your device's theme setting</div>
                        </div>
                      </div>
                    </label>

                    <label className={`theme-option ${themeMode === 'light' ? 'checked' : ''}`}>
                      <input
                        type="radio"
                        name="theme"
                        value="light"
                        checked={themeMode === 'light'}
                        onChange={() => handleThemeChange('light')}
                      />
                      <div className="theme-option-content">
                        <div className="theme-option-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="5"/>
                            <line x1="12" y1="1" x2="12" y2="3"/>
                            <line x1="12" y1="21" x2="12" y2="23"/>
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                            <line x1="1" y1="12" x2="3" y2="12"/>
                            <line x1="21" y1="12" x2="23" y2="12"/>
                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                          </svg>
                        </div>
                        <div className="theme-option-text">
                          <div className="theme-option-title">Light mode</div>
                          <div className="theme-option-description">Always use light theme</div>
                        </div>
                      </div>
                    </label>

                    <label className={`theme-option ${themeMode === 'dark' ? 'checked' : ''}`}>
                      <input
                        type="radio"
                        name="theme"
                        value="dark"
                        checked={themeMode === 'dark'}
                        onChange={() => handleThemeChange('dark')}
                      />
                      <div className="theme-option-content">
                        <div className="theme-option-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                          </svg>
                        </div>
                        <div className="theme-option-text">
                          <div className="theme-option-title">Dark mode</div>
                          <div className="theme-option-description">Always use dark theme</div>
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Done Button */}
                  <div className="settings-actions">
                    <button
                      type="button"
                      className="btn-modern btn-primary"
                      onClick={handleDone}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* Account Tab */}
              {activeTab === 'account' && (
                <div className="settings-section">
                  <div className="section-header">
                    <h3 className="section-title">Danger Zone</h3>
                    <p className="section-description">Irreversible and destructive actions</p>
                  </div>
                  <button
                    type="button"
                    className="btn-modern btn-danger"
                    onClick={() => setShowDeleteAccountModal(true)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    Delete Account
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteAccountModal show={showDeleteAccountModal} onHide={() => setShowDeleteAccountModal(false)} />
    </div>
  )
}

export default Settings
